export type ApuracaoStatus = 'ABERTO' | 'FECHADO' | 'REABERTO';

export type TipoCliente = 'AVULSO' | 'CONVENIADO';

export type EventoStatus = 'APURADO' | 'NAO_COBRAVEL' | 'TRIAL_FREE' | 'TRIAL_CHARGE' | 'REJEITADO';

export type CategoriaEvento =
  | 'NOTIFICACAO_ENVIADA'
  | 'LOTE_NOTIFICACAO_CONCLUIDO'
  | 'NEGOCIACAO_COM_VALOR'
  | 'NEGOCIACAO_SEM_VALOR'
  | 'AR_ENVIADO'
  | 'REENVIO_POSTAL'
  | 'SEDEX'
  | 'TAXA_SUCESSO'
  | 'CONSUMO_PACOTE'
  | 'BASE_MENSAL_HIBRIDO';

export type MotivoRejeicao =
  | 'SEM_CONTRATO'
  | 'CATEGORIA_DESCONHECIDA'
  | 'FORA_PERIODO'
  | 'DUPLICADO'
  | 'PAYLOAD_INVALIDO'
  | 'BLOQUEIO_TRIAL';

export type ProdutoPeriodicidade = 'MENSAL' | 'ANUAL' | 'AVULSO' | 'HIBRIDO';

export type ProdutoTipoUso = 'POR_USO' | 'ASSINATURA' | 'PACOTE';

export type ModalidadePostal =
  | 'CHARGED_SEPARATELY'
  | 'INCLUDED_IN_PACKAGE'
  | 'CUSTOMER_CHOICE_ON_ONBOARDING';

export type BoletoCorreio = 'SEPARADO' | 'CORRELACIONADO';

export type TrialBehavior = 'FREE' | 'CHARGE' | 'BLOCK';

export type OrigemEventoTipo =
  | 'NOTIFICACAO'
  | 'LOTE'
  | 'NEGOCIACAO'
  | 'AR'
  | 'CORREIO'
  | 'SISTEMA'
  | 'EXTERNAL';

export type ApuracaoAuditoriaAcao =
  | 'CICLO_CRIADO'
  | 'EVENTO_APURADO'
  | 'EVENTO_REJEITADO'
  | 'EVENTO_REPROCESSADO'
  | 'CICLO_FECHADO'
  | 'CICLO_REABERTO'
  | 'PACOTE_ALERTA'
  | 'FATURA_EMITIDA';

export interface SnapshotContrato {
  contratoId: string;
  valorCustomizado: number | null;
  percentualDesconto: number | null;
  modalidadePostal: ModalidadePostal | null;
  boletoCorreio: BoletoCorreio | null;
  modalidade: string | null;
}

export type EventoItemStatus = 'SUCESSO' | 'PENDENTE' | 'FALHA' | 'INFO';

export interface EventoItemDetalhado {
  id: string;
  label: string;
  descricao: string | null;
  status: EventoItemStatus | null;
  statusLabel: string | null;
  valor: number | null;
  metadata: Record<string, string> | null;
}

export interface EventoConsumo {
  id: string;
  apuracaoId: string | null;
  tenantId: string;
  tenantNome: string;
  produtoId: string | null;
  produtoNome: string | null;
  categoria: CategoriaEvento;
  status: EventoStatus;
  timestamp: string;
  quantidade: number;
  valorUnitario: number | null;
  valorCobravel: number;
  descontoAplicado: number;
  origemId: string;
  origemTipo: OrigemEventoTipo;
  origemLabel: string;
  motivoRejeicao: MotivoRejeicao | null;
  sugestaoCorrecao: string | null;
  snapshotContrato: SnapshotContrato | null;
  payloadBruto: Record<string, unknown>;
  itensDetalhados: EventoItemDetalhado[] | null;
  itensTituloRotulo: string | null;
}

export interface CategoriaBreakdown {
  categoria: CategoriaEvento;
  quantidade: number;
  valorUnitarioMedio: number | null;
  subtotal: number;
  descontoTotal: number;
  eventosIds: string[];
}

export interface ApuracaoAuditoriaEntry {
  id: string;
  acao: ApuracaoAuditoriaAcao;
  usuarioId: string | null;
  usuarioNome: string | null;
  timestamp: string;
  descricao: string;
  detalhes: Record<string, unknown> | null;
}

export interface SaldoPacote {
  total: number;
  consumido: number;
  percentualConsumido: number;
  proximoReset: string;
  alertaPercentual: number;
}

export interface ConfigHibridoApuracao {
  monthlyBasePrice: number;
  includedUnits: number;
  unidadesUsadas: number;
  overageUnitPrice: number;
  unidadesExcedentes: number;
  valorExcedenteTotal: number;
}

/** Segunda métrica do ciclo — franquia, consumo e excedente de MENSAGEM. */
export interface ConfigMensagemApuracao {
  mensagensIncluidas: number;
  mensagensUsadas: number;
  mensagensExcedentes: number;
  valorMensagemExcedente: number;
  valorExcedenteTotal: number;
}

export interface TrialContextApuracao {
  trialId: string;
  comportamento: TrialBehavior;
  dataExpiracao: string;
}

export interface Apuracao {
  id: string;
  tenantId: string;
  tenantNome: string;
  contratoId: string;
  produtoId: string;
  produtoNome: string;
  produtoTipoUso: ProdutoTipoUso;
  produtoPeriodicidade: ProdutoPeriodicidade;
  tipoCliente: TipoCliente;
  diaVencimento: number;
  periodoInicio: string;
  periodoFim: string;
  status: ApuracaoStatus;
  totalCobravel: number;
  totalEventos: number;
  eventosRejeitadosCount: number;
  eventosNaoCobraveisCount: number;
  breakdown: CategoriaBreakdown[];
  eventos: EventoConsumo[];
  auditoria: ApuracaoAuditoriaEntry[];
  saldoPacote: SaldoPacote | null;
  configHibrido: ConfigHibridoApuracao | null;
  configMensagem: ConfigMensagemApuracao | null;
  trialContext: TrialContextApuracao | null;
  pacoteAlerta: boolean;
  faturaEmitida: boolean;
  fechadoEm: string | null;
  fechadoPor: string | null;
  reabertoEm: string | null;
  reabertoPor: string | null;
  justificativaReabertura: string | null;
  criadoEm: string;
  atualizadoEm: string;
}

export interface ApuracaoListFilters {
  busca: string;
  status: ApuracaoStatus[];
  tipoCliente: TipoCliente[];
  periodoInicio: string | null;
  periodoFim: string | null;
  somenteComErros: boolean;
}

export interface ApuracaoListSummary {
  totalAbertos: number;
  vencendoEmAteSeteDias: number;
  fechadosNoMes: number;
  comErros: number;
  totalCobravelPrevisto: number;
}

export interface FecharCicloFormData {
  apuracaoId: string;
  cienteEventosRejeitados: boolean;
}

export interface ReabrirCicloFormData {
  apuracaoId: string;
  justificativa: string;
  cienteImpacto: boolean;
}

export interface ReprocessarEventosFormData {
  eventosIds: string[];
}

export interface EventosRejeitadosFilters {
  busca: string;
  motivos: MotivoRejeicao[];
  tenantId: string | null;
  periodoInicio: string | null;
  periodoFim: string | null;
  categorias: CategoriaEvento[];
}

export interface RejectedInvoice {
  invoiceId: string;
  number: string;
  status: string;
  totalValue: number;
  asaasError: string | null;
  asaasStatus: string | null;
  issuedAt: string;
}

export interface ApuracoesState {
  apuracoes: Apuracao[];
  summary: ApuracaoListSummary;
  filters: ApuracaoListFilters;
  eventosRejeitadosFilters: EventosRejeitadosFilters;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

export const DEFAULT_APURACAO_FILTERS: ApuracaoListFilters = {
  busca: '',
  status: [],
  tipoCliente: [],
  periodoInicio: null,
  periodoFim: null,
  somenteComErros: false,
};

export const DEFAULT_APURACAO_SUMMARY: ApuracaoListSummary = {
  totalAbertos: 0,
  vencendoEmAteSeteDias: 0,
  fechadosNoMes: 0,
  comErros: 0,
  totalCobravelPrevisto: 0,
};

export const DEFAULT_EVENTOS_REJEITADOS_FILTERS: EventosRejeitadosFilters = {
  busca: '',
  motivos: [],
  tenantId: null,
  periodoInicio: null,
  periodoFim: null,
  categorias: [],
};
